/** Phase 3A — Owner cross-user account switcher.
 *
 * Owners can switch to view any workspace member's data. The choice is
 * persisted to localStorage as `tm_view_as_user_id` and read by
 * `useViewAsUser()` hook + attached to `x-view-as-user` header by axios.
 *
 * Traders never see this switcher — the button hides based on role.
 * Backend enforces read-only permission — hiding UI is not enough.
 */
import { useEffect, useState } from "react";
import { Users, X, Eye } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/lib/auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { qk } from "@/lib/queryKeys";

const STORAGE_KEY = "tm_view_as_user_id";

export function getViewAsUserId() {
  try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
}

export function subscribeViewAsUser(handler) {
  const listener = () => handler(getViewAsUserId());
  window.addEventListener("tm:view-as-changed", listener);
  return () => window.removeEventListener("tm:view-as-changed", listener);
}

function setViewAsUserId(id) {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
  window.dispatchEvent(new Event("tm:view-as-changed"));
}

export default function AccountSwitcher() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [viewingId, setViewingId] = useState(getViewAsUserId());

  useEffect(() => subscribeViewAsUser(setViewingId), []);

  const { data: rawMembers = [] } = useQuery({
    queryKey: qk(["members"], { userScoped: false }),
    queryFn: async () => {
      const r = await api.get("/workspace/members");
      const list = r.data?.members || r.data || [];
      // Normalise: /api/workspace/members returns {id,email,name,role,...}
      // AccountSwitcher operates on `user_id` — map so the rest of the file
      // does not have to know about the payload shape.
      return list.map((m) => ({ ...m, user_id: m.user_id || m.id }));
    },
    staleTime: 5 * 60_000,
    enabled: user?.role === "owner",
  });
  const members = rawMembers;

  if (user?.role !== "owner") return null;
  if (!members?.length || members.length <= 1) return null;

  const viewingMember = members.find((m) => m.user_id === viewingId);
  const isViewingSelf = !viewingId || viewingId === user.id;

  const setViewer = (id) => {
    setViewAsUserId(id === user.id ? null : id);
    // Clear per-user cached queries so the switch is clean.
    qc.invalidateQueries();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`chip !text-[10px] uppercase border-current transition-colors focus:outline-none focus:ring-1 focus:ring-current ${
            isViewingSelf ? "text-muted-foreground hover:text-foreground" : "text-signal-yellow"
          }`}
          data-testid="account-switcher-btn"
          aria-label={isViewingSelf ? "Switch account view" : `Currently viewing ${viewingMember?.email || "another trader"} in read-only mode`}
        >
          {isViewingSelf ? <Users size={11} className="inline mr-1"/> : <Eye size={11} className="inline mr-1"/>}
          {isViewingSelf ? "View" : (viewingMember?.email?.split("@")[0] || "viewing")}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="end" data-testid="account-switcher-popover">
        <div className="p-2 border-b border-border/50 label-cap">View As</div>
        <div className="p-1 max-h-72 overflow-y-auto">
          <button
            onClick={() => setViewer(user.id)}
            className={`w-full text-left px-2 py-1.5 hover:bg-secondary/40 text-xs mono flex items-center gap-2 focus:outline-none focus:ring-1 focus:ring-current ${isViewingSelf ? "bg-secondary/40" : ""}`}
            data-testid="account-switcher-self"
          >
            <div className={`w-1.5 h-1.5 ${isViewingSelf ? "bg-signal-green" : "bg-transparent"}`} aria-hidden="true"/>
            <div className="flex-1">
              <div>{user.email} (you)</div>
              <div className="text-[10px] text-muted-foreground uppercase">owner</div>
            </div>
          </button>
          {members.filter((m) => m.user_id !== user.id).map((m) => {
            const active = viewingId === m.user_id;
            return (
              <button
                key={m.user_id}
                onClick={() => setViewer(m.user_id)}
                className={`w-full text-left px-2 py-1.5 hover:bg-secondary/40 text-xs mono flex items-center gap-2 focus:outline-none focus:ring-1 focus:ring-current ${active ? "bg-secondary/40" : ""}`}
                data-testid={`account-switcher-member-${m.user_id}`}
              >
                <div className={`w-1.5 h-1.5 ${active ? "bg-signal-yellow" : "bg-transparent"}`} aria-hidden="true"/>
                <div className="flex-1">
                  <div>{m.email}</div>
                  <div className="text-[10px] text-muted-foreground uppercase">{m.role} · read-only</div>
                </div>
              </button>
            );
          })}
        </div>
        <div className="p-2 text-[10px] text-muted-foreground border-t border-border/50">
          Viewing another trader is read-only. Approvals, rejections, order submissions and modifications remain restricted to the account owner.
        </div>
      </PopoverContent>
    </Popover>
  );
}


// ---- Read-only banner ----
export function ReadOnlyBanner() {
  const { user } = useAuth();
  const [viewingId, setViewingId] = useState(getViewAsUserId());
  const [members, setMembers] = useState([]);

  useEffect(() => subscribeViewAsUser(setViewingId), []);
  useEffect(() => {
    if (user?.role !== "owner" || !viewingId) return;
    api.get("/workspace/members").then((r) => {
      const list = r.data?.members || r.data || [];
      setMembers(list.map((m) => ({ ...m, user_id: m.user_id || m.id })));
    }).catch(() => {});
  }, [user, viewingId]);

  if (user?.role !== "owner" || !viewingId || viewingId === user.id) return null;
  const target = members.find((m) => m.user_id === viewingId);
  const label = target?.email?.split("@")[0] || "another trader";

  return (
    <div
      className="border-b border-signal-yellow/60 bg-signal-yellow/10 px-3 sm:px-4 py-1.5 flex items-center gap-2 text-xs text-signal-yellow"
      role="status"
      aria-live="polite"
      data-testid="read-only-banner"
    >
      <Eye size={12} aria-hidden="true"/>
      <span className="mono uppercase text-[10px]">Viewing {label}&apos;s account — Read Only</span>
      <button
        onClick={() => setViewAsUserId(null)}
        className="ml-auto text-[10px] mono uppercase underline underline-offset-2 hover:opacity-80 focus:outline-none focus:ring-1 focus:ring-current"
        data-testid="read-only-banner-exit"
      >
        <X size={11} className="inline mr-0.5"/>Exit view
      </button>
    </div>
  );
}
